import { put, call, select, takeLatest } from 'redux-saga/effects';
import {
  FETCH_POLICY_CARDS_SUCCESS,
  FETCH_POLICY_CARDS,
  FETCH_GO_GREEN_POLICY_LIST_SUCCESS,
  SUBMIT_GO_GREEN_POLICY_LIST,
  SUBMIT_GO_GREEN_POLICY_LIST_SUCCESS,
  VERIFY_CODE,
  RESEND_CODE,
  RESEND_CODE_SUCCESS,
  RESEND_CODE_FAIL,
  UPDATE_CHANGE_CONTACT_LOADING,
  DISPLAY_ERROR_CODE,
  UPDATE_VERIFY_LOADING,
} from '../actions/changeContactAction';
import uuidv4 from 'uuid/v4';
import {
  stringFormatter,
  clearEmptyParams,
  cleanObject,
  filterByRegex,
} from '../../../utils';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import {
  getContactsPermissionsApi,
  getSelectedPolicy,
  getPoliciesApi,
} from '../selectors/changeContactSelector';
import {
  getChangeContactLabelsConfig,
  getOtpApi,
  getVerifyOtpApi,
} from '../selectors/changeContactSelector';
import { getLanguage } from '../../../selectors/userSelectors';
import { showErrorModal } from '../../../actions/uiActions';
import { resolveLocalizedText, isApp } from '../../../utils';
import {
  callBackErrorCodePage,
  returnErrorCode,
  sortPermissionPolicies,
  returnAddressHorizontal,
} from '../utils';
import { contactPermissionPolicy } from '../types/changeContactService';
import _ from 'lodash';
import { VERIFY_DISPLAY_ERROR_CODE_LIST } from '../constants/constants';
import { getFilterPolicyListByRegex } from '../../policy-list/selectors/policyListSelectors';
import { store } from '../../../stores/index';
import {
  UserTypeCodes,
  ContactInfoStatuses,
  ENGLISH_ADDRESS_CASE,
  CHINESE_ADDRESS_CASE,
  ADDRESS_DEFAULT_VALUE,
} from '../constants/constants';
import {
  UserTypes,
  ContactInfoStatusType,
} from '../types/changeContactService';
import { EntityCode } from '../../../types/entityCodeTypes';
import { ENTITY_CODE } from '../../../constants/constants';

const emailKey = 'email';
const mobileKey = 'mobile';
const addressKey = 'address';

function getValueText(
  LabelsConfig: any,
  {
    type,
    mobile,
    mobileStatus,
    email,
    emailStatus,
    address,
    addressStatus,
  }: {
    type: string;
    mobile?: string;
    email?: string;
    address?: string;
    mobileStatus?: ContactInfoStatusType;
    emailStatus?: ContactInfoStatusType;
    addressStatus?: ContactInfoStatusType;
  },
) {
  const { updateInProgress, provideMobile, provideEmail, invalidAddress } =
    LabelsConfig;
  const updateInProgressText = updateInProgress
    ? resolveLocalizedText(updateInProgress)
    : '';
  const provideMobileText = provideMobile
    ? resolveLocalizedText(provideMobile)
    : '';
  const provideEmailText = provideEmail
    ? resolveLocalizedText(provideEmail)
    : '';
  const invalidAddressText = invalidAddress
    ? resolveLocalizedText(invalidAddress)
    : '';
  if (type === emailKey) {
    const emailText =
      emailStatus === ContactInfoStatuses.VALID
        ? email
        : emailStatus === ContactInfoStatuses.UPDATING
        ? updateInProgressText
        : emailStatus === ContactInfoStatuses.EMPTY
        ? provideEmailText
        : '';
    return emailText;
  } else if (type === mobileKey) {
    const mobileText =
      mobileStatus === ContactInfoStatuses.VALID
        ? mobile
        : mobileStatus === ContactInfoStatuses.UPDATING
        ? updateInProgressText
        : mobileStatus === ContactInfoStatuses.EMPTY
        ? provideMobileText
        : '';
    return mobileText;
  } else if (type === addressKey) {
    const addressText =
      addressStatus === ContactInfoStatuses.UPDATING
        ? updateInProgressText
        : addressStatus === ContactInfoStatuses.VALID
        ? returnAddressHorizontal(address)
        : addressStatus === ContactInfoStatuses.INVALID
        ? invalidAddressText
        : '';
    return addressText;
  }
  return '';
}

function* fetchPolicyCardsData(payload: any) {
  try {
    yield put({ type: UPDATE_CHANGE_CONTACT_LOADING, isLoading: true });
    const policyUrl = yield select(getPoliciesApi);
    // const res = {
    //   status: 200,
    //   json: () => require("../mock/changeContact.json"),
    // };
    if (!policyUrl) {
      return;
    }
    const res = yield call(
      myAxaFetchWithToken,
      stringFormatter(policyUrl, {
        featureName: 'changecontact',
      }),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-axahk-msgid': uuidv4(),
        },
      },
    );
    const data = yield res.json();
    // filter and display LHK/LMA policies only for web-portal
    const regex = yield select(getFilterPolicyListByRegex);
    const response = isApp()
      ? data
      : filterByRegex(data, regex, 'policy') || [];

    // status:  0: valid / 1: updating / 2: empty
    // userType: 0: Life / 1: EB / 2: P&C
    const LabelsConfig = yield select(getChangeContactLabelsConfig);
    const language = yield select(getLanguage) || 'en';
    const policyCards = response.map((item: any = {}) => {
      const {
        policy = {},
        policyRiders = [],
        contactDetails = {},
        addressCase,
        inEligibleAddressUpdateReasonCode,
        inEligibleEmailMobileUpdateReasonCode,
      } = item;
      const { policyNumber, certificateNumber, dependent = '' } = policy;
      const entityCode: EntityCode = policy.entityCode;
      const {
        emailAddress = '',
        emailUpdateStatus,
        mobileNumber = '',
        mobileNumberUpdateStatus,
        address = {},
        address_zh = {},
        addressUpdateStatus,
        suspensionFlagEmail,
        suspensionFlagSms,
      }: {
        emailAddress: string;
        mobileNumber: string;
        emailUpdateStatus: string;
        mobileNumberUpdateStatus: string;
        address: any;
        address_zh: any;
        addressUpdateStatus: string;
        suspensionFlagEmail: string;
        suspensionFlagSms: string;
      } = contactDetails;
      const mobileStatus: ContactInfoStatusType =
        mobileNumberUpdateStatus === 'P'
          ? ContactInfoStatuses.UPDATING
          : mobileNumber
          ? ContactInfoStatuses.VALID
          : ContactInfoStatuses.EMPTY;
      const emailStatus: ContactInfoStatusType =
        emailUpdateStatus === 'P'
          ? ContactInfoStatuses.UPDATING
          : emailAddress
          ? ContactInfoStatuses.VALID
          : ContactInfoStatuses.EMPTY;
      const addressStatus: ContactInfoStatusType =
        addressUpdateStatus === 'P'
          ? ContactInfoStatuses.UPDATING
          : addressCase === ENGLISH_ADDRESS_CASE ||
            addressCase === CHINESE_ADDRESS_CASE
          ? ContactInfoStatuses.VALID
          : ContactInfoStatuses.INVALID;
      const userType: UserTypes = UserTypeCodes[entityCode];
      const mobileDisplay = userType !== UserTypeCodes.EHK;
      const buttonNotCanClick = (): boolean => {
        switch (userType) {
          case UserTypeCodes.LHK:
            return (
              mobileStatus === ContactInfoStatuses.UPDATING &&
              emailStatus === ContactInfoStatuses.UPDATING &&
              addressStatus === ContactInfoStatuses.UPDATING
            );
          case UserTypeCodes.GHK:
            return (
              mobileStatus === ContactInfoStatuses.UPDATING &&
              emailStatus === ContactInfoStatuses.UPDATING
            );
          case UserTypeCodes.G4HK:
            return (
              mobileStatus === ContactInfoStatuses.UPDATING &&
              emailStatus === ContactInfoStatuses.UPDATING
            );
          case UserTypeCodes.EHK:
            return emailStatus === ContactInfoStatuses.UPDATING;
          default:
            return true;
        }
      };
      const addressValue =
        addressCase === ENGLISH_ADDRESS_CASE
          ? address
          : addressCase === CHINESE_ADDRESS_CASE
          ? address_zh
          : ADDRESS_DEFAULT_VALUE;
      return {
        planName: _.get(policy, `policyName[${language}]`),
        policyNumber,
        certificateNumber,
        entityCode,
        mobileDisplay,
        mobile: mobileNumber || '',
        mobileText: getValueText(LabelsConfig, {
          type: mobileKey,
          mobile: mobileNumber,
          mobileStatus,
        }),
        mobileStatus,
        suspensionFlagSms,
        email: emailAddress,
        emailText: getValueText(LabelsConfig, {
          type: emailKey,
          email: emailAddress,
          emailStatus,
        }),
        emailStatus,
        suspensionFlagEmail,
        buttonNotCanClick: buttonNotCanClick(),
        dependent,
        userType,
        addressCase,
        address: addressValue,
        addressStatus,
        addressText: getValueText(LabelsConfig, {
          type: addressKey,
          address: addressValue,
          addressStatus,
        }),
        inEligibleAddressUpdateReasonCode:
          inEligibleAddressUpdateReasonCode || '',
        inEligibleEmailMobileUpdateReasonCode:
          inEligibleEmailMobileUpdateReasonCode || '',
        policy,
      };
    });
    yield put({ type: FETCH_POLICY_CARDS_SUCCESS, policyCards });
    yield put({ type: UPDATE_CHANGE_CONTACT_LOADING, isLoading: false });
  } catch (e) {
    yield put({ type: UPDATE_CHANGE_CONTACT_LOADING, isLoading: false });
    yield put(showErrorModal());
  }
}

export function* sendOtpRequest({
  featureName,
  countryCode,
  email,
  mobile,
  userId,
  password,
}: {
  featureName: string;
  email?: string;
  countryCode?: string;
  mobile?: string;
  userId?: string;
  password?: string;
}) {
  const language = yield select(getLanguage);
  const api = yield select(getOtpApi);
  const url = stringFormatter(api, {
    featureName,
  });
  // const res = {
  //   status: 200,
  //   json: () => require("../mock/otpResponse.json"),
  // };
  const res = yield call(
    myAxaFetchWithToken,
    url,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-axahk-msgid': uuidv4(),
      },
      body: JSON.stringify(
        cleanObject({
          locale: language.toUpperCase(),
          email,
          countryCode,
          mobile,
          userId,
          password,
          platform: isApp() ? 'APP' : 'WEB',
        }),
      ),
    },
    undefined,
    undefined,
    0,
  );
  const data = yield res.json();
  return data;
}

export function* fetchOtherPolicyList(
  featureName: string,
  recordContactPermission: Object,
  isRenewalUpdate: boolean,
) {
  try {
    const selectedPolicy = yield select(getSelectedPolicy);
    const { policyNumber, policy } = selectedPolicy;
    const contactsPermissionsApi = yield select(getContactsPermissionsApi);

    // const res = {
    //   status: 200,
    //   json: () => require("../mock/otherPolicyList.json"),
    // };
    const res = yield call(
      myAxaFetchWithToken,
      clearEmptyParams(
        stringFormatter(contactsPermissionsApi, {
          featureName,
        }),
      ),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-axahk-msgid': uuidv4(),
        },
        body: JSON.stringify({
          recordContactPermission: {
            policyNumber,
            entityCode: policy.entityCode,
            ...(policy.entityCode === 'EHK'
              ? {
                  certificateNumber: policy.certificateNumber,
                  dependentNumber: policy.dependentNumber,
                }
              : {}),
            ...recordContactPermission,
          },
        }),
      },
      undefined,
      undefined,
      0,
    );
    const data = yield res.json();

    let response =
      (data &&
        data.retrieveCustomerContactsPermissionsResponse &&
        data.retrieveCustomerContactsPermissionsResponse.policies) ||
      [];

    // filter and display LHK/LMA policies only
    const regex = yield select(getFilterPolicyListByRegex);
    response = isApp() ? response : filterByRegex(response, regex, '') || [];

    // const mappedPolicyNameRes = response.map((policy: any) => {
    //   const foundPolicy = policyList.find(
    //     (policyB) =>
    //       policyB.policyNumber ===
    //       [policy.certificateNumber, policy.policyNumber]
    //         .filter((p) => p)
    //         .join("-")
    //   );

    //   return { ...policy, policyName: foundPolicy && foundPolicy.policyName };
    // });
    const sortedRes = sortPermissionPolicies(response).filter((policy) => {
      if (isRenewalUpdate) {
        return policy.entityCode === 'GHK';
      }
      return policy;
    });

    return sortedRes;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function* fetchGoGreenPolicyList(
  selectedList: contactPermissionPolicy[],
  otherPolicyList: any[],
) {
  try {
    const selectedPolicy = yield select(getSelectedPolicy);
    const selectedMainPolicy = otherPolicyList.filter(
      (policy: contactPermissionPolicy) =>
        policy.policyNumber === selectedPolicy.policyNumber,
    );
    const goGreenPolicyList = [...selectedMainPolicy, ...selectedList].filter(
      (policy: contactPermissionPolicy) => policy.eCommEligible === 'Y',
    );

    yield put({
      type: FETCH_GO_GREEN_POLICY_LIST_SUCCESS,
      goGreenPolicyList,
    });
    return goGreenPolicyList;
  } catch (e) {
    yield put(showErrorModal());
  }
}

function* submitGoGreenPolicyList(payload: any) {
  try {
    const { goGreenPolicyList } = payload;
    const res = {
      status: 200,
      json: () => require('../mock/submitGoGreenResponse.json'),
    };
    // const url = `${customerProfileFeatureUrl}?device=WEB`;
    // let res = yield call(myAxaFetchWithToken, url, {
    //     method: 'GET',
    //     headers: {
    //         'Content-Type': 'application/json',
    //         'x-axahk-msgid': uuidv4()
    //     }
    // });
    const data = yield res.json();
    const response = data || [];

    yield put({
      type: SUBMIT_GO_GREEN_POLICY_LIST_SUCCESS,
    });
    return response;
  } catch (e) {
    yield put(showErrorModal());
  }
}

function* verifyCode(action: {
  type: string;
  payload: {
    featureName: string;
    otp: string;
    callback: (...args: any[]) => any;
    defaultErrorCode: string;
    featureId: string;
  };
}) {
  const { featureName, otp, callback, defaultErrorCode, featureId } =
    action.payload;
  try {
    // const api = yield select(getVerifyOtpApi);
    // const url = stringFormatter(api, {
    //   featureName,
    //   otp,
    //   platform: isApp() ? "APP" : "WEB",
    // });
    // yield put({ type: UPDATE_VERIFY_LOADING, isVerifyLoading: true });
    // const res = {
    //   status: 200,
    //   json: () => require("../mock/verifyCodeResponse.json"),
    // };
    // const res = yield call(myAxaFetchWithToken, url, {
    //   method: 'GET',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     "x-axahk-msgid": uuidv4(),
    //   }
    // }, undefined, undefined, 0);
    // yield put({ type: UPDATE_VERIFY_LOADING, isVerifyLoading: false });
    // const data = yield res.json();
    // const errorCode = _.get(data, 'response.reasonCode', '');
    // handleErrorCode(errorCode, callback, defaultErrorCode, featureId)

    /* Remove verify API call due to security concern, this function will simply trigger callback
       API side will check OTP in final submission to avoid keeping otp for more than 15 min
     */
    if (_.isFunction(callback)) {
      callback();
    }
  } catch (error) {
    // yield put(showErrorModal());
    yield put({ type: UPDATE_VERIFY_LOADING, isVerifyLoading: false });
    if (error && error.json) {
      error.json().then((data: any) => {
        const errorCode = _.get(data, 'response.reasonCode', defaultErrorCode);
        handleErrorCode(errorCode, callback, defaultErrorCode, featureId);
      });
    } else {
      handleErrorCode(defaultErrorCode, callback, defaultErrorCode, featureId);
    }
  }
}

export function handleErrorCode(
  errorCode: string,
  callback: (...args: any[]) => any,
  defaultErrorCode: string,
  featureId: string,
) {
  if (_.isEmpty(errorCode) || errorCode === '200') {
    if (_.isFunction(callback)) {
      callback();
    }
  } else {
    const isDisplayError = VERIFY_DISPLAY_ERROR_CODE_LIST.includes(errorCode);
    if (isDisplayError) {
      store.dispatch({
        type: DISPLAY_ERROR_CODE,
        errorCode: errorCode,
      });
    } else {
      callBackErrorCodePage(
        featureId,
        returnErrorCode(errorCode, defaultErrorCode, featureId),
      );
    }
  }
}

export function* resendOtpRequest({
  featureName,
  email,
  countryCode,
  mobile,
}: {
  featureName: string;
  email?: string;
  countryCode?: string;
  mobile?: string;
}) {
  try {
    yield put({
      type: RESEND_CODE,
    });
    // const res = {
    //   status: 200,
    //   json: () => require("../mock/resendVerifyCodeResponse.json"),
    // };
    // const response = yield otpRes.json();
    const otpRes = yield call(sendOtpRequest, {
      featureName,
      email,
      countryCode,
      mobile,
    });

    yield put({
      type: RESEND_CODE_SUCCESS,
    });
  } catch (e) {
    yield put({
      type: RESEND_CODE_FAIL,
    });
    yield put(showErrorModal());
  }
}

function* changeContactSaga() {
  yield takeLatest(FETCH_POLICY_CARDS, fetchPolicyCardsData);
  yield takeLatest(SUBMIT_GO_GREEN_POLICY_LIST, submitGoGreenPolicyList);
  yield takeLatest(VERIFY_CODE, verifyCode);
}

export default changeContactSaga;
